import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";

import { LoadingPage } from "@/components/LoadingPage";
import { RestrictedAccess } from "@/components/RestrictedAccess";
import { getErrorMessage } from "@/lib/error";
import { useServiceContext } from "@/services";
import type { MondayBoardContext } from "@/services/monday";

const CurrentMondayContext = createContext<MondayBoardContext | null>(null);

// This component makes the Monday context available to its child components.
// Child components MUST use method `useCurrentMondayContext` to access it:
// This avoids that every components calls the same Monday API methods without knowing other
// components already did the same.
export function WithMondayContextAvailable({ children }: PropsWithChildren) {
  const { monday } = useServiceContext();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isViewOnly, setViewOnly] = useState(false);
  const [currentContext, setCurrentContext] = useState<MondayBoardContext>();

  useEffect(() => {
    monday
      .fetchBoardContext()
      .then((context) => {
        if (context.user?.isViewOnly || context.user?.isGuest) {
          setViewOnly(true);
          return;
        }
        setCurrentContext(context);
      })
      .catch((error) => {
        setErrorMessage(getErrorMessage(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [monday]);

  if (isLoading) {
    return <LoadingPage label="Loading…" />;
  }

  if (isViewOnly) {
    return <RestrictedAccess />;
  }

  if (!currentContext) {
    throw new Error(`Failed to initialize item context: ${errorMessage || "unknown error"}`);
  }

  return <CurrentMondayContext.Provider value={currentContext}>{children}</CurrentMondayContext.Provider>;
}

export function useCurrentMondayContext() {
  const context = useContext(CurrentMondayContext);
  if (!context) {
    throw new Error("Context not initialized");
  }
  return context;
}
